/**
 * Playing a coach's board script out loud, in time with the board.
 *
 * The sync is structural, not timed: each step carries one sentence and the
 * position that belongs with it, so the board is set when the sentence starts
 * and the next step waits for the speech to finish. Nothing here estimates how
 * long a sentence takes to say — that is the thing that drifts.
 *
 * The speaker is injected so this can be tested without a speech engine, and
 * so a better voice can replace the browser's without touching the stepping.
 */

export type CoachStep = {
  say: string;
  move: string | null;
  san: string | null;
  fen: string;
  highlight: string[];
  arrows: [string, string][] | string[][];
};

/** Speaks one utterance and resolves when it has finished (or failed). */
export type Speaker = {
  speak: (text: string) => Promise<void>;
  cancel: () => void;
};

/**
 * The browser's own speech synthesis.
 *
 * Chosen over a server voice for the first cut because `onend` is what makes
 * the board wait for the sentence — an audio round-trip per step would add a
 * pause before every move, which is precisely the desync being avoided.
 */
export function browserSpeaker(): Speaker {
  const synth =
    typeof window !== 'undefined' ? (window as any).speechSynthesis : null;

  return {
    speak: (text: string) =>
      new Promise<void>(resolve => {
        if (!synth || typeof (window as any).SpeechSynthesisUtterance !== 'function') {
          // No voice available: the board should still advance, just silently.
          // A fixed beat is better than stopping the demonstration dead.
          setTimeout(resolve, Math.min(6000, 900 + text.length * 45));
          return;
        }
        try {
          const utterance = new (window as any).SpeechSynthesisUtterance(text);
          utterance.rate = 0.95;
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };
          utterance.onend = finish;
          // Some browsers drop `onend` when speech is interrupted or when the
          // tab is backgrounded; without a floor the script would hang on that
          // step forever.
          utterance.onerror = finish;
          setTimeout(finish, 20000);
          synth.speak(utterance);
        } catch {
          resolve();
        }
      }),
    cancel: () => {
      try {
        synth?.cancel();
      } catch {
        /* nothing to cancel */
      }
    },
  };
}

/**
 * Walk a script, one step at a time.
 *
 * `onStep` is called with the step to show *before* its sentence is spoken, so
 * the board is already right when the voice starts. Returns a function that
 * stops playback; calling it mid-step prevents any later step from showing.
 */
export function playScript(
  steps: CoachStep[],
  speaker: Speaker,
  onStep: (step: CoachStep | null, index: number) => void,
): () => void {
  let cancelled = false;

  const run = async () => {
    for (let i = 0; i < steps.length; i++) {
      if (cancelled) return;
      onStep(steps[i], i);
      await speaker.speak(steps[i].say);
      if (cancelled) return;
    }
    // Leave the final position on the board; only the highlight goes.
    onStep(null, steps.length);
  };

  run();

  return () => {
    cancelled = true;
    speaker.cancel();
  };
}
